import { setHtml } from "./dom";

export const VIETNAM_TIMEZONE_OFFSET_MS = 7 * 60 * 60 * 1000;

export const pad = (value: number): string => String(value).padStart(2, "0");

export const toGmt7Iso = (date: Date): string => {
  const shifted = new Date(date.getTime() + VIETNAM_TIMEZONE_OFFSET_MS);
  return `${shifted.getUTCFullYear()}-${pad(shifted.getUTCMonth() + 1)}-${pad(shifted.getUTCDate())}T${pad(shifted.getUTCHours())}:${pad(shifted.getUTCMinutes())}:${pad(shifted.getUTCSeconds())}+07:00`;
};

export const currentMatchWindow = () => {
  const nowGmt7 = new Date(Date.now() + VIETNAM_TIMEZONE_OFFSET_MS);
  const year = nowGmt7.getUTCFullYear();
  const month = nowGmt7.getUTCMonth();
  const date = nowGmt7.getUTCDate();

  const fromUtc = Date.UTC(year, month, date - 1, 20 - 7, 0, 0);
  const toUtc = Date.UTC(year, month, date + 1, 12 - 7, 0, 0);

  return {
    fromDate: toGmt7Iso(new Date(fromUtc)),
    toDate: toGmt7Iso(new Date(toUtc)),
  };
};

export const currentFixtureDefaultWindow = currentMatchWindow;

export const normalizeFixtureDate = (value?: string): string => {
  const raw = String(value || "").trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;

  const nowGmt7 = new Date(Date.now() + VIETNAM_TIMEZONE_OFFSET_MS);
  const year = nowGmt7.getUTCFullYear();
  const month = pad(nowGmt7.getUTCMonth() + 1);
  const day = pad(nowGmt7.getUTCDate());
  return `${year}-${month}-${day}`;
};

export const currentFixtureWindow = (
  dateValue?: string,
): { fromDate: string; toDate: string } => {
  if (!String(dateValue || "").trim()) return currentFixtureDefaultWindow();

  const date = normalizeFixtureDate(dateValue);
  const [year, month, day] = date.split("-").map(Number);
  const fromUtc = Date.UTC(year, month - 1, day, -7, 0, 0);
  const toUtc = Date.UTC(year, month - 1, day, 16, 59, 59);

  return {
    fromDate: toGmt7Iso(new Date(fromUtc)),
    toDate: toGmt7Iso(new Date(toUtc)),
  };
};

export const formatTime = (value?: string) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleTimeString("vi-VN", {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Asia/Ho_Chi_Minh",
  });
};

export const formatFtClock = (info: any): string => {
  const time = formatTime(info?.matchTime);
  return time === "-" ? "FT" : `FT ${time}`;
};

export const elapsedMinute = (value?: string): number | null => {
  if (!value) return null;
  const start = new Date(value);
  if (Number.isNaN(start.getTime())) return null;
  return Math.max(0, Math.floor((Date.now() - start.getTime()) / 60000) + 1);
};

const reportedMatchMinute = (info: any, statusId: number): number | null => {
  const raw = info?.minute ?? info?.matchMinute ?? info?.minutes;
  const minute = Number(raw);
  if (!Number.isFinite(minute) || minute < 0) return null;
  return statusId === 4 && minute <= 45 ? 45 + minute : minute;
};

const secondHalfMinute = (info: any): number | null => {
  const secondHalfElapsed = elapsedMinute(info?.matchTime2);
  if (secondHalfElapsed !== null) return 45 + secondHalfElapsed;
  const reported = reportedMatchMinute(info, 4);
  if (reported !== null) return reported;
  const kickoffElapsed = elapsedMinute(info?.matchTime);
  return kickoffElapsed === null ? null : Math.max(46, kickoffElapsed - 15);
};

export const createMatchClockFormatter =
  (labels: Record<number, string>) =>
  (info: any): string => {
    const statusId = Number(info?.statusId);
    const matchStatusLabel = (id: number): string => labels[id] || "-";
    if (statusId === 1) return formatTime(info?.matchTime);
    if (statusId === 2) {
      const minute = elapsedMinute(info?.matchTime);
      const displayMinute = minute ?? reportedMatchMinute(info, 2);
      if (displayMinute === null) return "H1";
      return `${Math.min(displayMinute, 45)}'${displayMinute > 45 ? "+" : ""}`;
    }
    if (statusId === 3) return "HT";
    if (statusId === 4) {
      const matchMinute = secondHalfMinute(info);
      if (matchMinute === null) return "H2";
      return `${Math.min(matchMinute, 90)}'${matchMinute > 90 ? "+" : ""}`;
    }
    if (statusId === 5 || statusId === 6 || statusId === 7)
      return matchStatusLabel(statusId);
    if (statusId === 8) return formatFtClock(info);
    return matchStatusLabel(statusId);
  };

export const createMatchClockHtmlFormatter =
  (labels: Record<number, string>) =>
  (info: any): string => {
    const statusId = Number(info?.statusId);
    if (statusId === 2) {
      const minute = elapsedMinute(info?.matchTime);
      const displayMinute = minute ?? reportedMatchMinute(info, 2);
      if (displayMinute === null) return "H1";
      return `${Math.min(displayMinute, 45)}<span class="gwd-livescore-board__minute-mark">'</span>${displayMinute > 45 ? "+" : ""}`;
    }
    if (statusId === 4) {
      const matchMinute = secondHalfMinute(info);
      if (matchMinute === null) return "H2";
      return `${Math.min(matchMinute, 90)}<span class="gwd-livescore-board__minute-mark">'</span>${matchMinute > 90 ? "+" : ""}`;
    }
    return createMatchClockFormatter(labels)(info);
  };

export const setMatchClock = (
  root: ParentNode,
  selector: string,
  info: any,
  labels: Record<number, string>,
) => {
  setHtml(root, selector, createMatchClockHtmlFormatter(labels)(info));
};

export const setMobileMatchStatusClock = (
  root: ParentNode,
  info: any,
  labels: Record<number, string>,
) => {
  const status = root.querySelector<HTMLElement>("[data-gwd-mobile-status]");
  const clock = root.querySelector<HTMLElement>("[data-gwd-mobile-clock]");
  if (Number(info?.statusId) === 8) {
    if (status && status.textContent !== "HT") status.textContent = "HT";
    if (clock && clock.textContent !== "FT") clock.textContent = "FT";
    return;
  }

  if (status && status.textContent !== "HT") status.textContent = "HT";
  if (clock) {
    const nextHtml = createMatchClockHtmlFormatter(labels)(info);
    if (clock.innerHTML !== nextHtml) clock.innerHTML = nextHtml;
  }
};

export const formatMatchDate = (value?: string) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("vi-VN", { day: "2-digit", month: "2-digit" });
};

export const formatMatchDateTitle = (value?: string) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  const dayNames = [
    "Chủ nhật",
    "Thứ hai",
    "Thứ ba",
    "Thứ tư",
    "Thứ năm",
    "Thứ sáu",
    "TThứ bảy",
  ];
  return `${date.toLocaleDateString("vi-VN", { day: "2-digit", month: "2-digit", year: "numeric" })} (${dayNames[date.getDay()]})`;
};
